import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { Navigate } from 'react-router-dom'
import { auditLogsApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Loading from '@/components/ui/Loading'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import Badge from '@/components/ui/Badge'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import {
  FileText,
  Search,
  Calendar,
  User,
  Trash2,
  Eye,
  Filter,
  X,
  Info,
} from 'lucide-react'
import { formatNumber } from '@/utils/format'

export default function AuditLogs() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Only Super Admins can access this page
  if (user?.user_type !== 'super_admin') {
    return <Navigate to="/dashboard" />
  }

  const [page, setPage] = useState(1)
  const [limit] = useState(50)

  // Filter input states (not applied until button clicked)
  const [searchInput, setSearchInput] = useState('')
  const [actionFilterInput, setActionFilterInput] = useState('')
  const [userIdFilterInput, setUserIdFilterInput] = useState('')
  const [dateFromInput, setDateFromInput] = useState('')
  const [dateToInput, setDateToInput] = useState('')

  // Applied filter states (used in query)
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [userIdFilter, setUserIdFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [selectedLog, setSelectedLog] = useState<any>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Build query params (memoized to prevent unnecessary re-renders)
  const queryParams = useMemo(() => {
    const params: any = { page, limit }
    if (search) params.search = search
    if (actionFilter) params.action_name = actionFilter
    if (userIdFilter) params.user_id = userIdFilter
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    return params
  }, [page, limit, search, actionFilter, userIdFilter, dateFrom, dateTo])

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', queryParams],
    queryFn: () => auditLogsApi.list(queryParams),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => auditLogsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
      setSuccess('Audit log deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete audit log')
    },
  })

  const handleApplyFilters = () => {
    // Apply all filter inputs to the actual filter states
    setSearch(searchInput)
    setActionFilter(actionFilterInput)
    setUserIdFilter(userIdFilterInput)
    setDateFrom(dateFromInput)
    setDateTo(dateToInput)
    setPage(1)
  }

  const handleClearFilters = () => {
    // Clear both input and applied states
    setSearchInput('')
    setActionFilterInput('')
    setUserIdFilterInput('')
    setDateFromInput('')
    setDateToInput('')
    setSearch('')
    setActionFilter('')
    setUserIdFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const handleViewDetails = (log: any) => {
    setSelectedLog(log)
    setShowDetailModal(true)
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this audit log entry? This action cannot be undone.')) {
      deleteMutation.mutate(id)
    }
  }

  const logs = data?.data?.logs || []
  const actionNames = data?.data?.action_names || []
  const pagination = data?.data?.pagination || { total: 0, page: 1, pages: 1 }

  const getActionBadgeVariant = (actionName: string) => {
    if (actionName.includes('login') || actionName.includes('register')) return 'success'
    if (actionName.includes('delete') || actionName.includes('purge')) return 'danger'
    if (actionName.includes('update') || actionName.includes('edit')) return 'warning'
    if (actionName.includes('create') || actionName.includes('add')) return 'primary'
    return 'gray'
  }

  if (isLoading) return <Loading />

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && <Alert type="success" message={success} />}
      {error && <Alert type="error" message={error} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center space-x-3">
            <FileText className="w-8 h-8 text-primary-600" />
            <span>Audit Logs</span>
          </h1>
          <p className="text-gray-600 mt-2">
            System-wide audit trail of all user actions and events
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Total Entries</p>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(pagination.total)}</p>
        </div>
      </div>

      {/* Filters */}
      <Card title="Filters">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="label">
                <Search className="w-4 h-4 inline mr-2" />
                Search
              </label>
              <input
                type="text"
                className="input"
                placeholder="Action, user, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
              />
            </div>

            {/* Action Filter */}
            <div>
              <label className="label">
                <Filter className="w-4 h-4 inline mr-2" />
                Action Type
              </label>
              <select
                className="input"
                value={actionFilterInput}
                onChange={(e) => setActionFilterInput(e.target.value)}
              >
                <option value="">All Actions</option>
                {actionNames.map((item: any) => (
                  <option key={item.action_name} value={item.action_name}>
                    {item.action_name}
                  </option>
                ))}
              </select>
            </div>

            {/* User ID Filter */}
            <div>
              <label className="label">
                <User className="w-4 h-4 inline mr-2" />
                User ID
              </label>
              <input
                type="number"
                className="input"
                placeholder="Filter by user ID"
                value={userIdFilterInput}
                onChange={(e) => setUserIdFilterInput(e.target.value)}
              />
            </div>

            {/* Date From */}
            <div>
              <label className="label">
                <Calendar className="w-4 h-4 inline mr-2" />
                From Date
              </label>
              <input
                type="date"
                className="input"
                value={dateFromInput}
                onChange={(e) => setDateFromInput(e.target.value)}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="label">
                <Calendar className="w-4 h-4 inline mr-2" />
                To Date
              </label>
              <input
                type="date"
                className="input"
                value={dateToInput}
                onChange={(e) => setDateToInput(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex space-x-3">
            <Button onClick={handleApplyFilters} variant="primary">
              <Filter className="w-4 h-4 mr-2" />
              Filter Records
            </Button>
            <Button onClick={handleClearFilters} variant="secondary">
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <ResponsiveTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="3">
                  User
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="2">
                  Action
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="1">
                  IP Address
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs.map((log: any) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(log.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="3" data-label="User">
                      {log.user_id ? (
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {log.user_first_name} {log.user_last_name}
                          </span>
                          <span className="text-xs text-gray-500">{log.user_email}</span>
                          <Badge variant={log.user_type === 'super_admin' ? 'danger' : log.user_type === 'admin' ? 'warning' : 'gray'} className="mt-1 inline-block w-fit">
                            {log.user_type}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">System</span>
                      )}
                    </td>
                    <td className="hide-mobile px-6 py-4" data-priority="2" data-label="Action">
                      <Badge variant={getActionBadgeVariant(log.action_name)}>
                        {log.action_name}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono" data-priority="1" data-label="IP Address">
                      {log.ip_address || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        className="mobile-details-trigger text-blue-600 hover:text-blue-900"
                        title="View Hidden Details"
                      >
                        <Info className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleViewDetails(log)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(log.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ResponsiveTable>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          title="Audit Log Details"
          size="lg"
        >
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Log ID</label>
                <p className="text-sm text-gray-900">{selectedLog.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Timestamp</label>
                <p className="text-sm text-gray-900">{new Date(selectedLog.created_at).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">User</label>
                <p className="text-sm text-gray-900">
                  {selectedLog.user_id ? (
                    <>
                      {selectedLog.user_first_name} {selectedLog.user_last_name} ({selectedLog.user_email})
                      <Badge variant={selectedLog.user_type === 'super_admin' ? 'danger' : 'gray'} className="ml-2">
                        {selectedLog.user_type}
                      </Badge>
                    </>
                  ) : (
                    'System'
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Action</label>
                <p className="text-sm text-gray-900">
                  <Badge variant={getActionBadgeVariant(selectedLog.action_name)}>
                    {selectedLog.action_name}
                  </Badge>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">IP Address</label>
                <p className="text-sm text-gray-900 font-mono">{selectedLog.ip_address || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email Sent</label>
                <p className="text-sm text-gray-900">
                  {selectedLog.email_sent ? (
                    <Badge variant="success">Yes</Badge>
                  ) : (
                    <Badge variant="gray">No</Badge>
                  )}
                </p>
              </div>
            </div>

            {/* User Agent */}
            {selectedLog.user_agent && (
              <div>
                <label className="text-sm font-medium text-gray-700">User Agent</label>
                <p className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded mt-1 break-all">
                  {selectedLog.user_agent}
                </p>
              </div>
            )}

            {/* Action Data */}
            {selectedLog.action_data_parsed && (
              <div>
                <label className="text-sm font-medium text-gray-700">Action Data</label>
                <pre className="text-xs text-gray-900 bg-gray-50 p-4 rounded mt-1 overflow-x-auto">
                  {JSON.stringify(selectedLog.action_data_parsed, null, 2)}
                </pre>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                onClick={() => handleDelete(selectedLog.id)}
                variant="danger"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Log
              </Button>
              <Button onClick={() => setShowDetailModal(false)} variant="secondary">
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
